import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import api from "@/api";
import { CompanySwitcher, ThemeToggle } from "@/store";
import LogoMark from "@/components/Logo";
import ErrorBoundary from "@/components/ErrorBoundary";
import ForcePasswordGate from "@/components/ForcePasswordGate";
import { usePlatformName } from "@/brand";
import { useEffect } from "react";
import NotificationBell from "@/components/NotificationBell";
import AttendanceWidget from "@/components/AttendanceWidget";
import CommandSearch from "@/components/CommandSearch";
import {
  LayoutDashboard, Wallet, Target, FileText, Receipt, ShoppingCart, CreditCard, Calendar, Image, Boxes, UserCheck,
  Banknote, CalendarOff, Award, Users, Settings as SettingsIcon, Search, LogOut, Menu, X, PanelLeftClose, PanelLeftOpen,
  UserRound, BriefcaseBusiness, BookOpen, ShieldCheck, ClipboardList, File, BarChart3, Star, Bell, Archive, CircleHelp, Wrench,
  ChevronLeft, MoreHorizontal, Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_GROUPS = [
  { label: "Overview", items: [
    { to: "/admin", end: true, key: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/money", key: "money", label: "All Money", icon: Wallet, roles: ["super_admin"] },
  ] },
  { label: "Sales", items: [
    { to: "/admin/leads", key: "leads", label: "Lead", icon: Target, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/quotations", key: "quotations", label: "Quotation", icon: FileText, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/billing", key: "billing", label: "Billing", icon: Receipt, roles: ["super_admin", "admin", "accountant"] },
  ] },
  { label: "Purchase & Spend", items: [
    { to: "/admin/purchase", key: "purchase", label: "Purchase", icon: ShoppingCart, roles: ["super_admin"] },
    { to: "/admin/expenses", key: "expenses", label: "Expenses", icon: CreditCard, roles: ["super_admin"] },
  ] },
  { label: "Operations", items: [
    { to: "/admin/calendar", key: "calendar", label: "Calendar", icon: Calendar, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/gallery", key: "gallery", label: "Gallery", icon: Image, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/stock", key: "stock", label: "Stock", icon: Boxes, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  ] },
  { label: "People", items: [
    { to: "/admin/attendance", key: "attendance", label: "Attendance", icon: UserCheck, roles: ["super_admin", "admin"] },
    { to: "/admin/payroll", key: "payroll", label: "Payroll", icon: Banknote, roles: ["super_admin"] },
    { to: "/admin/leaves", key: "leaves", label: "Leave", icon: CalendarOff, roles: ["super_admin", "admin", "telecaller", "accountant"] },
    { to: "/admin/incentives", key: "incentives", label: "Incentive", icon: Award, roles: ["super_admin", "admin"] },
    { to: "/admin/hr", key: "hr", permissionKey: "users", label: "HR", icon: Users, roles: ["super_admin", "admin"] },
  ] },
  { label: "System", items: [
    { to: "/admin/settings", key: "settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin", "admin"] },
  ] },
];

const MORE_GROUP = { label: "More", items: [
  { to: "/admin/myhr", key: "myhr", label: "My HR", icon: UserRound, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/customers", key: "customers", label: "Customers", icon: Users, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/vendors", key: "vendors", label: "Vendors / Suppliers", icon: ShoppingCart, roles: ["super_admin"] },
  { to: "/admin/products", key: "catalog", label: "Services & Products Catalog", icon: Boxes, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/jobs", key: "jobs", label: "Jobs / Orders", icon: BriefcaseBusiness, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/amc-warranty", key: "amc_warranty", label: "AMC & Warranty", icon: ShieldCheck, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/tasks", key: "tasks_reminders", label: "Tasks & Reminders", icon: ClipboardList, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/documents", key: "documents", label: "Documents", icon: File, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/reports", key: "reports", label: "Reports", icon: BarChart3, roles: ["super_admin", "admin", "accountant"] },
  { to: "/admin/feedback", key: "feedback", label: "Feedback & Ratings", icon: Star, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/notifications", key: "notifications", label: "Notifications Center", icon: Bell, roles: ["super_admin", "admin", "telecaller", "accountant"] },
  { to: "/admin/audit-log", key: "audit_logs", label: "Audit Log", icon: BookOpen, roles: ["super_admin"] },
  { to: "/admin/recycle-bin", key: "recycle_bin", label: "Recycle Bin", icon: Archive, roles: ["super_admin"] },
  { to: "/admin/help-support", key: "help_support", label: "Help & Support", icon: CircleHelp, roles: ["super_admin", "admin", "telecaller", "accountant"] },
] };

const CONSOLE_GROUP = { label: "Platform Console", items: [
  { to: "/admin/platform", key: "platform", label: "Platform", icon: ShieldCheck },
  { to: "/admin/workspace", key: "workspace", label: "Workspace Studio", icon: Wrench },
  { to: "/admin/diagnostics", key: "diagnostics", label: "Diagnostics", icon: BookOpen },
] };

const FINANCIAL_KEYS = new Set(["money", "purchase", "expenses", "payroll", "vendors"]);
const MORE_KEYS = new Set(MORE_GROUP.items.map((item) => item.key));
const DEFAULT_SIDEBAR_MODULES = [...new Set([
  ...NAV_GROUPS.flatMap((group) => group.items.map((item) => item.key)),
  ...MORE_GROUP.items.map((item) => item.key),
])].map((key, index) => ({ key, label: key, enabled: true, order: index, more_order: index }));

const ROLE_LABELS = { super_admin: "Owner", admin: "Admin", telecaller: "Telecaller", accountant: "Accountant", team_leader: "Field Staff" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const platformName = usePlatformName();
  const [tenantConfig, setTenantConfig] = useState(user.tenant?.config || {});
  const [configError, setConfigError] = useState(false);
  const isPlatformConsole = user.role === "super_admin" && !user.active_tenant_id;
  const nav = useNavigate();
  const loc = useLocation();
  const [impersonating] = useState(() => sessionStorage.getItem("cm_impersonating"));
  const sidebarKey = `cm_sidebar_collapsed_${user.id}`;
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(sidebarKey) === "true");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  useEffect(() => {
    setTenantConfig(user.tenant?.config || {});
    setConfigError(false);
    if (!isPlatformConsole) {
      api.get("/workspace/config").then((response) => {
        setTenantConfig(response.data.config);
        setConfigError(false);
      }).catch(() => setConfigError(true));
    }
    const updateConfig = (event) => { if (event.detail) setTenantConfig(event.detail); };
    window.addEventListener("workspace-config-updated", updateConfig);
    return () => window.removeEventListener("workspace-config-updated", updateConfig);
  }, [user.id, user.tenant?.id, user.tenant?.config, isPlatformConsole]);
  const retryConfig = () => api.get("/workspace/config").then((response) => {
    setTenantConfig(response.data.config);
    setConfigError(false);
  }).catch(() => setConfigError(true));
  const exitImpersonation = async () => {
    try { await api.post("/auth/impersonate-exit"); } catch (e) { console.warn("impersonation exit failed", e); }
    sessionStorage.removeItem("cm_impersonating");
    window.location.href = "/admin/users";
  };
  const cfgModules = tenantConfig.modules && tenantConfig.modules.length ? tenantConfig.modules : DEFAULT_SIDEBAR_MODULES;
  const moduleByKey = Object.fromEntries(cfgModules.map((m) => [m.key, m]));
  const perms = user.permissions || {};
  const isWorkspaceOwner = user.role === "super_admin" || user.is_workspace_owner;
  const moreItems = MORE_GROUP.items
    .filter((item) => moduleByKey[item.key]?.enabled !== false)
    .filter((item) => !item.roles || item.roles.includes(user.role) || (isWorkspaceOwner && ["vendors", "recycle_bin"].includes(item.key)) || (perms[item.permissionKey || item.key] || []).includes("view"))
    .filter((item) => !FINANCIAL_KEYS.has(item.key) || isWorkspaceOwner || (perms[item.permissionKey || item.key] || []).includes("view"))
    .map((item) => {
      const industry = user.tenant?.industry_type || "service";
      if (item.key === "jobs_orders") {
        const configuredLabel = moduleByKey[item.key]?.label;
        const defaultLabel = configuredLabel === "Jobs / Orders" || !configuredLabel;
        return { ...item, to: industry === "product" ? "/admin/orders" : "/admin/jobs",
          label: defaultLabel ? (industry === "product" ? "Orders" : "Jobs") : configuredLabel };
      }
      return { ...item, label: moduleByKey[item.key]?.label || item.label };
    })
    .sort((a, b) => (moduleByKey[a.key]?.more_order ?? MORE_GROUP.items.findIndex((item) => item.key === a.key)) - (moduleByKey[b.key]?.more_order ?? MORE_GROUP.items.findIndex((item) => item.key === b.key)));
  const visibleGroups = (isPlatformConsole ? [CONSOLE_GROUP] : [...NAV_GROUPS, { ...MORE_GROUP, items: moreItems }]).map((group) => ({
    ...group,
    items: group.items
      .filter((item) => !item.roles || item.roles.includes(user.role) || (isWorkspaceOwner && FINANCIAL_KEYS.has(item.key)) || (perms[item.key] || []).includes("view"))
      .filter((item) => !FINANCIAL_KEYS.has(item.key) || isWorkspaceOwner || (perms[item.key] || []).includes("view"))
      .filter((item) => {
        const permissionKey = item.permissionKey || item.key;
        return !(permissionKey in perms) || (perms[permissionKey] || []).includes("view");
      })
      .filter((item) => !MORE_KEYS.has(item.key) || moduleByKey[item.key]?.enabled !== false),
  })).filter((group) => group.items.length);
  const showGroupLabels = tenantConfig.sidebar?.show_group_labels !== false;
  const allVisibleItems = visibleGroups.flatMap((group) => group.items);
  const quickItems = ["dashboard", "leads", "billing", "attendance"]
    .map((key) => allVisibleItems.find((item) => item.key === key))
    .filter(Boolean);
  const activePage = allVisibleItems
    .filter((item) => loc.pathname === item.to || (item.to !== "/admin" && loc.pathname.startsWith(`${item.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  const quickActions = [
    { key: "leads", label: "New lead", icon: Target, to: "/admin/leads" },
    { key: "quotations", label: "New quotation", icon: FileText, to: "/admin/quotations" },
    { key: "billing", label: "New invoice", icon: Receipt, to: "/admin/billing" },
    { key: "expenses", label: "New expense", icon: CreditCard, to: "/admin/expenses" },
  ].filter((action) => allVisibleItems.some((item) => item.key === action.key));

  useEffect(() => { localStorage.setItem(sidebarKey, String(collapsed)); }, [sidebarKey, collapsed]);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);
  return (
    <div className="min-h-screen flex" data-testid="admin-layout">
      {mobileOpen && <button aria-label="Close navigation" onClick={() => setMobileOpen(false)} className="fixed inset-0 z-30 bg-black/45 md:hidden" />}
      <aside className={cn("w-60 shrink-0 bg-[hsl(222,47%,11%)] text-slate-100 flex flex-col fixed inset-y-0 z-40 transition-[width,transform] duration-200", collapsed && "md:w-[4.5rem]", mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0")}>
        <div className="px-5 py-5 flex items-center gap-2.5 border-b border-white/10">
          <LogoMark size={32} />
          <div className={cn("min-w-0", collapsed && "md:hidden")}>
            <div className="font-heading font-bold text-sm leading-tight flex items-center gap-1.5" data-testid="sidebar-tenant-name">
              {user.tenant?.brand_color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: user.tenant.brand_color }} />}
              {user.tenant?.name || platformName}
            </div>
            <div className="text-[10px] text-slate-400 tracking-widest uppercase">Powered by {platformName}</div>
          </div>
          <button type="button" onClick={() => setCollapsed((value) => !value)} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="ml-auto hidden md:inline-flex p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white">
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
          <button type="button" onClick={() => setMobileOpen(false)} aria-label="Close navigation" className="ml-auto p-1.5 rounded hover:bg-white/10 md:hidden"><X size={17} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5" data-testid="admin-nav">
          {visibleGroups.map((group, groupIndex) => (
            <section key={group.label} className={cn(groupIndex > 0 && "mt-3 border-t border-white/15 pt-3")}>
              {showGroupLabels && !collapsed && <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{group.label}</div>}
              {group.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} title={collapsed ? n.label : undefined} data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) => cn(
                    "relative flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150",
                    collapsed && "md:justify-center md:px-0",
                    isActive ? "bg-primary/20 text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-primary" : "text-slate-300 hover:bg-white/10 hover:text-white"
                  )}>
                  <n.icon size={17} className="shrink-0" /> <span className={cn(collapsed && "md:hidden")}>{n.label}</span>
                </NavLink>
              ))}
            </section>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className={cn("flex items-center gap-3 px-2 py-2", collapsed && "md:justify-center md:px-0")}>
            <div className="w-9 h-9 rounded-full bg-blue-500/30 flex items-center justify-center font-heading font-bold text-blue-300">
              {user.name?.[0]}
            </div>
            <div className={cn("flex-1 min-w-0", collapsed && "md:hidden")}>
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-blue-300" data-testid="user-role-badge">{user.is_workspace_owner ? "Owner" : ROLE_LABELS[user.role]}</div>
            </div>
            <button data-testid="admin-logout-button" onClick={async () => { if (await logout()) nav("/login"); }}
              className="text-slate-400 hover:text-white transition-colors" title="Logout">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <main className={cn("min-w-0 flex-1 min-h-screen bg-background transition-[margin] overflow-x-clip", collapsed ? "md:ml-[4.5rem]" : "md:ml-60")}>
        {impersonating && (
          <div className="bg-amber-500 text-amber-950 text-sm font-semibold px-6 py-2 flex items-center justify-between" data-testid="impersonation-banner">
            <span>Viewing as {impersonating} — you are seeing exactly what they see</span>
            <button onClick={exitImpersonation} className="underline font-bold" data-testid="exit-impersonation-button">Exit</button>
          </div>
        )}
        {user.is_platform_admin && user.active_tenant_id && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky-500/30 bg-sky-500/10 px-6 py-2 text-sm" data-testid="active-workspace-banner">
            <span>Viewing: <strong>{user.tenant?.name || "Company workspace"}</strong></span>
            <button type="button" onClick={() => {
              localStorage.removeItem("cm_active_tenant");
              window.location.assign("/admin/platform");
            }} className="font-semibold text-primary underline underline-offset-2" data-testid="back-to-platform-console">
              Back to Platform Console
            </button>
          </div>
        )}
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b px-3 sm:px-6 py-2.5 flex items-center gap-2 sm:gap-3 flex-wrap">
          <button type="button" aria-label="Open navigation" onClick={() => setMobileOpen(true)} className="p-2 rounded-md hover:bg-accent md:hidden"><Menu size={18} /></button>
          <button type="button" aria-label="Go back" onClick={() => nav(-1)} className="p-2 rounded-md hover:bg-accent md:hidden"><ChevronLeft size={18} /></button>
          <h1 className="min-w-0 truncate text-sm font-semibold sm:text-base" data-testid="current-page-title">{activePage?.label || platformName}</h1>
          <CompanySwitcher />
          <div className="flex-1" />
          <AttendanceWidget />
          <button onClick={() => window.dispatchEvent(new Event("open-search"))} data-testid="global-search-button"
            className="flex items-center gap-2 text-xs text-muted-foreground border rounded-md px-3 py-1.5 hover:bg-accent transition-colors">
            <Search size={13} /> Search… <kbd className="font-mono text-[10px] bg-muted px-1 py-0.5 rounded">Ctrl K</kbd>
          </button>
          <ThemeToggle />
          <NotificationBell />
        </header>
        {configError && !isPlatformConsole && (
          <div role="alert" className="mx-6 mt-4 flex flex-wrap items-center justify-between gap-3 border border-rose-500/40 bg-rose-500/5 px-4 py-3 text-sm" data-testid="workspace-config-retry">
            <span>Workspace modules could not be loaded. The default sidebar remains available.</span>
            <button type="button" onClick={retryConfig} className="inline-flex items-center gap-2 font-semibold text-primary" data-testid="retry-workspace-config">Retry</button>
          </div>
        )}
        <div key={loc.pathname} className="p-6 pb-20 md:pb-6 max-w-[1400px] w-full mx-auto page-enter overflow-x-clip">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </div>
        {quickActionsOpen && <>
          <button type="button" aria-label="Close quick actions" onClick={() => setQuickActionsOpen(false)} className="fixed inset-0 z-30 bg-black/20 md:hidden" />
          <div role="menu" aria-label="Create new" className="fixed bottom-[calc(4.25rem+env(safe-area-inset-bottom))] left-1/2 z-40 w-[min(20rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border bg-popover p-2 shadow-layered md:hidden" data-testid="mobile-quick-actions">
            {quickActions.map(({ key, label, icon: Icon, to }) => <button key={key} type="button" role="menuitem" onClick={() => { setQuickActionsOpen(false); nav(to); }}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm hover:bg-accent"><Icon size={17} />{label}</button>)}
            {!isWorkspaceOwner && !user.is_platform_admin && <button type="button" role="menuitem" onClick={() => { setQuickActionsOpen(false); window.dispatchEvent(new Event("attendance-action")); }}
              className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-sm hover:bg-accent"><UserCheck size={17} />Check in / out</button>}
          </div>
        </>}
        <nav aria-label="Quick navigation" className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-6 border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
          {quickItems.slice(0, 2).map((item) => <NavLink key={item.key} to={item.to} end={item.end} aria-label={item.label} title={item.label}
            className={({ isActive }) => cn("flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground", isActive && "text-primary")}>
            <item.icon size={18} /><span className="max-w-full truncate px-1">{item.label}</span>
          </NavLink>)}
          <button type="button" aria-label="Create new" aria-expanded={quickActionsOpen} onClick={() => setQuickActionsOpen((value) => !value)}
            className="relative -mt-4 flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-primary text-primary-foreground shadow-layered transition-transform active:scale-95"><Plus size={21} /></span>
            <span>New</span>
          </button>
          {quickItems.slice(2, 4).map((item) => <NavLink key={item.key} to={item.to} end={item.end} aria-label={item.label} title={item.label}
            className={({ isActive }) => cn("flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground", isActive && "text-primary")}>
            <item.icon size={18} /><span className="max-w-full truncate px-1">{item.label}</span>
          </NavLink>)}
          <button type="button" aria-label="Open more navigation" onClick={() => setMobileOpen(true)}
            className="flex min-h-14 flex-col items-center justify-center gap-1 text-[10px] text-muted-foreground"><MoreHorizontal size={19} /><span>More</span></button>
        </nav>
        <CommandSearch />
        <ForcePasswordGate />
      </main>
    </div>
  );
}
